/**
 * Favicon pulse — animates the browser tab favicon ONLY while the user is
 * away from the page (tab hidden / window blurred). When the user returns
 * the favicon reverts to the static shield, so the "live" state is calm.
 *
 * How it works:
 *   • On `visibilitychange` → `document.hidden` true, start an interval
 *     that swaps the favicon `<link>` href across 3 pre-rendered pulse
 *     frames every 600 ms (heartbeat tempo).
 *   • On visibility restored, stop the interval and set the href back to
 *     the static shield.
 *
 * Zero deps, safe SSR (typeof window check), and idempotent (re-binding the
 * listener during HMR won't stack intervals).
 */
const STATIC_ICON = "/favicon-32.png";
const PULSE_FRAMES = [
  "/favicon-pulse-0.png",
  "/favicon-pulse-1.png",
  "/favicon-pulse-2.png",
  "/favicon-pulse-1.png", // reverse so the loop feels like a pulse, not a loop
];
const PULSE_INTERVAL_MS = 600;

let intervalId = null;
let frameIdx = 0;
let listenerBound = false;

function getOrCreateFaviconLink() {
  let link = document.querySelector('link[rel="icon"][data-pulse-target]');
  if (!link) {
    // Use the highest-priority existing icon link if present.
    link = document.querySelector('link[rel="icon"][type="image/png"]') ||
           document.querySelector('link[rel="icon"]');
  }
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/png";
    document.head.appendChild(link);
  }
  link.setAttribute("data-pulse-target", "true");
  return link;
}

function setFaviconHref(href) {
  const link = getOrCreateFaviconLink();
  // Cache-bust on frame change so browsers actually repaint the tab icon.
  link.href = `${href}?v=${Date.now()}`;
}

function startPulse() {
  if (intervalId) return;
  frameIdx = 0;
  intervalId = setInterval(() => {
    setFaviconHref(PULSE_FRAMES[frameIdx % PULSE_FRAMES.length]);
    frameIdx += 1;
  }, PULSE_INTERVAL_MS);
}

function stopPulse() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  setFaviconHref(STATIC_ICON);
}

export function initFaviconPulse() {
  if (typeof window === "undefined") return;
  if (listenerBound) return;
  listenerBound = true;
  const onVis = () => {
    if (document.hidden) startPulse();
    else stopPulse();
  };
  document.addEventListener("visibilitychange", onVis);
  // Also treat window blur as "away" on some desktop browsers where
  // `visibilitychange` fires less aggressively (e.g. user moved to another
  // app rather than another tab).
  window.addEventListener("blur", startPulse);
  window.addEventListener("focus", stopPulse);
}
